import React from "react";

import Loading from "ui/Loading";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";

import { Link } from "react-router-dom";
import { useRoutes } from "utils/routes";
import { useQueryAdvisorBlankSlateQuery } from "./gql/Query.generated";
import { IntegrationCheckList } from "features/Explain/QueryExplainsBlankSlate";

const QueryAdvisorBlankSlate = ({
  databaseId,
  serverId,
}: {
  databaseId: string;
  serverId: string;
}) => {
  const { databaseWorkbooksNew } = useRoutes();
  const { data, loading, error } = useQueryAdvisorBlankSlateQuery({
    variables: { serverId },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const systemType = data.getServerDetails.systemType;
  const insightConfig = {
    autoExplainMinDuration: data.autoExplainMinDuration?.value,
    logMinDurationStatement: "",
    sharedPreloadLibraries: data.sharedPreloadLibraries?.value,
    sessionPreloadLibraries: data.sessionPreloadLibraries?.value,
    logLinePrefix: data.logLinePrefix?.value,
  };
  const autoExplainMinDurationDisabled =
    data.autoExplainMinDuration?.value === "-1";
  const logInsightsIntegrated = data.getServerDetails.integratedLogInsights;
  const explainThresholdSettingValue = data.autoExplainMinDuration?.value;
  const explainThresholdSet = explainThresholdSettingValue != null;
  const explainAnalyzeSettingValue = data.autoExplainAnalyze?.value;
  const explainFormatSettingValue = data.autoExplainFormat?.value;

  const exceedBlurb = explainThresholdSet ? (
    <>
      {explainThresholdSettingValue} (your{" "}
      <code>auto_explain.log_min_duration</code> setting)
    </>
  ) : (
    <>
      your <code>auto_explain.log_min_duration</code> setting
    </>
  );

  const settingsOk =
    logInsightsIntegrated &&
    explainThresholdSet &&
    !autoExplainMinDurationDisabled &&
    explainAnalyzeSettingValue == "on" &&
    explainFormatSettingValue == "json";

  return (
    <Panel title="Queries with Insights (0)">
      <PanelSection>
        <p>
          Query Advisor did not find insights in the collected EXPLAIN Plan
          Samples. You can instead{" "}
          <Link to={databaseWorkbooksNew(databaseId)}>create a workbook</Link>{" "}
          to review specific queries.
        </p>
        {autoExplainMinDurationDisabled ? (
          <p>
            You must configure <code>auto_explain.log_min_duration</code> to
            capture EXPLAIN plans for Query Advisor insights.
          </p>
        ) : (
          <p>
            Please note that only query executions that exceed {exceedBlurb}{" "}
            will produce an EXPLAIN plan that is analyzed for insights.
          </p>
        )}
        {settingsOk || (
          <IntegrationCheckList
            serverId={serverId}
            systemType={systemType}
            config={insightConfig}
            logInsightsIntegrated={logInsightsIntegrated}
            explainAnalyzeSettingValue={explainAnalyzeSettingValue}
            explainFormatSettingValue={explainFormatSettingValue}
            queryAdvisor
          />
        )}
      </PanelSection>
    </Panel>
  );
};

export default QueryAdvisorBlankSlate;
